/**
 * Path-based routing for the SEO landing pages.
 * Must stay in sync with functions/lib/pages.ts (server side).
 *
 *   /                              -> worldwide, all categories
 *   /trending/{region}            -> region, all categories
 *   /trending/{region}/{category} -> region + category
 */

export const REGION_SLUGS: Record<string, string> = {
  ALL: 'worldwide',
  US: 'united-states',
  GB: 'united-kingdom',
  IN: 'india',
  JP: 'japan',
  KR: 'south-korea',
  BR: 'brazil',
  DE: 'germany',
  FR: 'france',
  CA: 'canada',
  AU: 'australia',
  MX: 'mexico',
  RU: 'russia',
};

export const CATEGORY_SLUGS: Record<string, string> = {
  '10': 'music',
  '17': 'sports',
  '20': 'gaming',
  '24': 'entertainment',
  '25': 'news',
};

const SLUG_TO_REGION: Record<string, string> = Object.fromEntries(
  Object.entries(REGION_SLUGS).map(([code, slug]) => [slug, code])
);
const SLUG_TO_CATEGORY: Record<string, string> = Object.fromEntries(
  Object.entries(CATEGORY_SLUGS).map(([id, slug]) => [slug, id])
);

import { type Lang, DEFAULT_LANG, isLang } from '../../functions/lib/i18n';

export type PageView = 'chart' | 'rising' | 'records' | 'stats' | 'channel' | 'channels' | 'compare-videos' | 'compare-channels';

// YouTube channel ids are always "UC" + 22 more characters.
const CHANNEL_ID_RE = /^UC[\w-]{22}$/;
const VIDEO_ID_RE = /^[\w-]{11}$/;

// "Best performers over the last N" windows (English-only). Keep in sync with
// WINDOW_SLUGS in functions/lib/pages.ts.
export const WINDOW_SLUGS: Record<string, string> = {
  'last-week': 'w1', 'last-2-weeks': 'w2', 'last-3-weeks': 'w3',
  'last-month': 'm1', 'last-3-months': 'm3', 'last-6-months': 'm6',
};
const SLUG_TO_WINDOW = WINDOW_SLUGS;
const WINDOW_TO_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(WINDOW_SLUGS).map(([slug, key]) => [key, slug])
);
export const WINDOW_LABEL: Record<string, string> = {
  w1: 'Last week', w2: 'Last 2 weeks', w3: 'Last 3 weeks',
  m1: 'Last month', m3: 'Last 3 months', m6: 'Last 6 months',
};
// Ordered list for building the timeframe selector.
export const WINDOW_ORDER = ['w1', 'w2', 'w3', 'm1', 'm3', 'm6'];

export interface RouteState {
  region: string;   // 'ALL' | 'US' | ...
  category: string; // 'ALL' | '10' | ...
  view: PageView;   // 'chart' (trending) | 'records' (all-time most viewed)
  lang: Lang;       // 'en' (root) | 'es' | 'pt' | 'de' | 'fr'
  window?: string;  // period key (w1..m6) for "best performers"; English only
  channelId?: string; // for view === 'channel'; English only
  compareIds?: [string, string];
}

/** Parse a pathname into region/category/view/lang, or null if not a known route. */
export function pathToState(pathname: string): RouteState | null {
  const clean = pathname.replace(/\/+$/, '') || '/';
  let parts = clean.split('/').filter(Boolean);

  // Optional leading language segment (English lives at the root).
  let lang: Lang = DEFAULT_LANG;
  if (parts.length && isLang(parts[0]) && parts[0] !== 'en') {
    lang = parts[0] as Lang;
    parts = parts.slice(1);
  }

  if (parts.length === 0) return { region: 'ALL', category: 'ALL', view: 'chart', lang };

  // Stats dashboard: /stats (English-only, no filters)
  if (parts[0] === 'stats') {
    return parts.length === 1 && lang === 'en'
      ? { region: 'ALL', category: 'ALL', view: 'stats', lang }
      : null;
  }

  // Individual channel page: /channel/{id} (English-only)
  if (parts[0] === 'channel') {
    return parts.length === 2 && lang === 'en' && CHANNEL_ID_RE.test(parts[1])
      ? { region: 'ALL', category: 'ALL', view: 'channel', lang, channelId: parts[1] }
      : null;
  }

  // Top-channels ranking hub: /channels (English-only)
  if (parts[0] === 'channels') {
    return parts.length === 1 && lang === 'en'
      ? { region: 'ALL', category: 'ALL', view: 'channels', lang }
      : null;
  }
  if (parts[0] === 'compare' && parts.length === 4 && lang === 'en') {
    const ids: [string, string] = [parts[2], parts[3]];
    if (parts[1] === 'videos' && ids.every((id) => VIDEO_ID_RE.test(id)) && ids[0] !== ids[1]) return { region: 'ALL', category: 'ALL', view: 'compare-videos', lang, compareIds: ids };
    if (parts[1] === 'channels' && ids.every((id) => CHANNEL_ID_RE.test(id)) && ids[0] !== ids[1]) return { region: 'ALL', category: 'ALL', view: 'compare-channels', lang, compareIds: ids };
    return null;
  }
  if (parts[0] === 'rising' && parts[1] === 'youtube' && lang === 'en') {
    const region = parts[2] ? SLUG_TO_REGION[parts[2]] : 'ALL';
    const category = parts[3] ? SLUG_TO_CATEGORY[parts[3]] : 'ALL';
    return region && category && parts.length <= 4 ? { region, category, view: 'rising', lang } : null;
  }

  // All-time records: /most-viewed and /most-viewed/{category}
  if (parts[0] === 'most-viewed') {
    if (parts.length === 1) return { region: 'ALL', category: 'ALL', view: 'records', lang };
    if (parts.length === 2) {
      const category = SLUG_TO_CATEGORY[parts[1]];
      return category ? { region: 'ALL', category, view: 'records', lang } : null;
    }
    return null;
  }

  if (parts[0] !== 'trending') return null;

  const rest = parts[1] === 'youtube' ? parts.slice(2) : parts.slice(1);
  // Optional trailing "best performers" window (English-only).
  let window: string | undefined;
  if (rest.length && SLUG_TO_WINDOW[rest[rest.length - 1]]) {
    if (lang !== 'en') return null;
    window = SLUG_TO_WINDOW[rest[rest.length - 1]];
    rest.pop();
  }

  if (rest.length === 0) return { region: 'ALL', category: 'ALL', view: 'chart', lang, window };
  if (rest.length === 1) {
    const region = SLUG_TO_REGION[rest[0]];
    return region ? { region, category: 'ALL', view: 'chart', lang, window } : null;
  }
  if (rest.length === 2) {
    const region = SLUG_TO_REGION[rest[0]];
    const category = SLUG_TO_CATEGORY[rest[1]];
    return region && category ? { region, category, view: 'chart', lang, window } : null;
  }
  return null;
}

/** Build the canonical path for a region/category/view/lang/window combination. */
export function stateToPath(
  region: string, category: string, view: PageView = 'chart', lang: Lang = DEFAULT_LANG, window?: string,
  channelId?: string, compareIds?: [string, string]
): string {
  // Window "best performers" pages are English-only and live under /trending.
  if (window && WINDOW_TO_SLUG[window]) {
    const base = category === 'ALL'
      ? `/trending/youtube/${REGION_SLUGS[region] || 'worldwide'}`
      : `/trending/youtube/${REGION_SLUGS[region] || 'worldwide'}/${CATEGORY_SLUGS[category]}`;
    return `${base}/${WINDOW_TO_SLUG[window]}`;
  }
  const p = lang === DEFAULT_LANG ? '' : `/${lang}`;
  if (view === 'stats') return '/stats';
  if (view === 'channels') return '/channels';
  if (view === 'channel') return channelId ? `/channel/${channelId}` : '/channels';
  if ((view === 'compare-videos' || view === 'compare-channels') && compareIds) return `/compare/${view === 'compare-videos' ? 'videos' : 'channels'}/${compareIds[0]}/${compareIds[1]}`;
  if (view === 'rising') {
    const base = `/rising/youtube/${REGION_SLUGS[region] || 'worldwide'}`;
    return category === 'ALL' ? base : `${base}/${CATEGORY_SLUGS[category]}`;
  }
  if (view === 'records') {
    return category === 'ALL' ? `${p}/most-viewed` : `${p}/most-viewed/${CATEGORY_SLUGS[category]}`;
  }
  if (category === 'ALL') {
    return region === 'ALL' ? p || '/' : `${p}/trending/youtube/${REGION_SLUGS[region]}`;
  }
  return `${p}/trending/youtube/${REGION_SLUGS[region] || 'worldwide'}/${CATEGORY_SLUGS[category]}`;
}
